import React, { useState, useEffect } from "react";
import { Modal } from "@material-ui/core";
import { isMobile } from "react-device-detect";
import { usePostHog } from 'posthog-js/react';
import { useSelector } from "react-redux";
import styles from "./CardPaymentBanner.module.scss";
import { CloseIcon } from "../../assets";

const CardPaymentBanner = () => {
  // Declare all hooks first - they must always be called in the same order
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [hasViewedFeatures, setHasViewedFeatures] = useState(false);
  const [viewedImages, setViewedImages] = useState(new Set());
  const [modalOpenTime, setModalOpenTime] = useState(null);
  const posthog = usePostHog();
  
  // Get current user from Redux state
  const user = useSelector((state) => state.auth.user);
  
  // PDF URL for download/view
  const pdfUrl = "https://intervend-ads.s3.eu-central-1.amazonaws.com/60e41665_70df_43c7_8aa7_21bd653f6dfc_Digitalizujte_svoju_autoperionicu.pdf";
  
  // Card payment terminal images - replace these URLs with your actual images
  const terminalImages = [
    {
      src: "https://via.placeholder.com/600x400/667eea/ffffff?text=Terminal+1",
      alt: "Kartični terminal - model 1",
      title: "Osnovni kartični terminal"
    },
    {
      src: "https://via.placeholder.com/600x400/764ba2/ffffff?text=Terminal+2", 
      alt: "All-in-one menjač sa terminalom",
      title: "All-in-one rešenje"
    },
    {
      src: "https://via.placeholder.com/600x400/28a745/ffffff?text=Terminal+3",
      alt: "Mobilni kartični terminal",
      title: "Mobilni terminal"
    },
    {
      src: "https://via.placeholder.com/600x400/ffc107/ffffff?text=Terminal+4",
      alt: "Terminal sa žetonjerom",
      title: "Terminal sa žetonjerom"
    },
    {
      src: "https://via.placeholder.com/600x400/dc3545/ffffff?text=Terminal+5",
      alt: "Kompaktni terminal",
      title: "Kompaktni model"
    }
  ];

  // Card payment features content
  const cardPaymentFeatures = [
    {
      icon: "🔧",
      title: "Opcija ugradnja na postojeće menjače",
      description: "Nadogradite svoje postojeće menjače sa modernim sistemom kartičnog plaćanja"
    },
    {
      icon: "💳",
      title: "All in one menjač sa terminalom i žetonjerom",
      description: "Kompletno rešenje u jednom uređaju - menjač, terminal i žetonjer"
    },
    {
      icon: "📦",
      title: "Ugradnja po boxovima",
      description: "Fleksibilna ugradnja prilagođena rasporedu vaših boxova"
    },
    {
      icon: "📊",
      title: "Praćenje kartičnog prometa",
      description: "Detaljni izveštaji i statistike svih kartičnih transakcija u realnom vremenu sa mogućnošću daljinskog storniranja transakcija i praćenja uspešnosti izdavanja žetona/vremena pranja"
    },
    {
      icon: "🎫",
      title: "Daljinsko izdavanje žetona",
      description: "Upravljajte izdavanjem žetona na daljinu kroz mobilnu aplikaciju i poklanjajte žetone sa bilo koje lokacije na klik"
    },
    {
      icon: "🧾",
      title: "Gotovinski računi",
      description: "Podrška za gotovinska plaćanja sa fiskalnim računima"
    },
    {
      icon: "🎨",
      title: "Prilagodljive boje terminala",
      description: "Sami možete da birate boje terminala da se prilagode vašoj perionici"
    },
    {
      icon: "⚖️",
      title: "Kompatibilno sa trenutnom fiskalizacijom",
      description: "Potpuna kompatibilnost sa postojećim sistemom koji koristite"
    },
    {
      icon: "🏆",
      title: "Mogućnost pranja i skupljanja poena preko aplikacije",
      description: "Integrisani program lojalnosti sa mogućnošću prikupljanja i korišćenja poena"
    }
  ];

  // Track banner view on component mount
  useEffect(() => {
    if (user?.showAd) {
      posthog?.capture('card_payment_banner_viewed', {
        device_type: isMobile ? 'mobile' : 'desktop',
        page_location: window.location.pathname,
        timestamp: new Date().toISOString()
      });
    }
  }, [posthog, user?.showAd]);

  // Handle back button when modal is open
  useEffect(() => {
    const handlePopState = (event) => {
      if (isModalOpen) {
        // Prevent default back navigation and close modal instead
        event.preventDefault();
        handleCloseModal();
        // Push current state back to prevent actual navigation
        window.history.pushState(null, '', window.location.href);
      }
    };

    if (isModalOpen) {
      // Push a dummy state when modal opens
      window.history.pushState(null, '', window.location.href);
      // Listen for back button
      window.addEventListener('popstate', handlePopState);
    }

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [isModalOpen]);

  useEffect(() => {
    if (isModalOpen && user?.showAd) {
      setModalOpenTime(Date.now());
      
      // Track modal open event
      posthog?.capture('card_payment_modal_opened', {
        device_type: isMobile ? 'mobile' : 'desktop',
        page_location: window.location.pathname,
        timestamp: new Date().toISOString()
      });
    }
  }, [isModalOpen, posthog, user?.showAd]);

  // Auto-play carousel with tracking
  useEffect(() => {
    if (!isModalOpen || !user?.showAd) return;
    
    const interval = setInterval(() => {
      const newIndex = currentImageIndex === terminalImages.length - 1 ? 0 : currentImageIndex + 1;
      setCurrentImageIndex(newIndex);
      setViewedImages(prev => new Set([...prev, newIndex]));
      
      // Track auto-advance
      posthog?.capture('card_payment_carousel_auto_advance', {
        device_type: isMobile ? 'mobile' : 'desktop',
        from_image_index: currentImageIndex,
        to_image_index: newIndex,
        image_title: terminalImages[newIndex].title,
        auto_advance_interval: 4000,
        timestamp: new Date().toISOString()
      });
    }, 4000);

    return () => clearInterval(interval);
  }, [isModalOpen, currentImageIndex, posthog, terminalImages, user?.showAd]);

  const handleBannerClick = () => {
    setIsModalOpen(true);
    
    // Track banner click event
    posthog?.capture('card_payment_banner_clicked', {
      device_type: isMobile ? 'mobile' : 'desktop',
      page_location: window.location.pathname,
      timestamp: new Date().toISOString()
    });
  };

  const handleCloseModal = () => {
    const modalOpenDuration = Date.now() - modalOpenTime;
    
    // Track modal close event with engagement metrics
    posthog?.capture('card_payment_modal_closed', {
      device_type: isMobile ? 'mobile' : 'desktop',
      modal_open_duration_seconds: Math.round(modalOpenDuration / 1000),
      viewed_features: hasViewedFeatures,
      images_viewed_count: viewedImages.size,
      total_images_available: terminalImages.length,
      final_image_index: currentImageIndex,
      timestamp: new Date().toISOString()
    });
    
    setIsModalOpen(false);
    setCurrentImageIndex(0);
    setHasViewedFeatures(false);
    setViewedImages(new Set());
  };

  const handleOpenPDF = () => {
    // Track PDF button click
    posthog?.capture('card_payment_pdf_clicked', {
      device_type: isMobile ? 'mobile' : 'desktop',
      pdf_url: pdfUrl,
      current_image_index: currentImageIndex,
      images_viewed_count: viewedImages.size,
      timestamp: new Date().toISOString()
    });
    
    window.open(pdfUrl, '_blank', 'noopener,noreferrer');
  };

  // Carousel navigation functions
  const nextImage = () => {
    const newIndex = currentImageIndex === terminalImages.length - 1 ? 0 : currentImageIndex + 1;
    setCurrentImageIndex(newIndex);
    setViewedImages(prev => new Set([...prev, newIndex]));
    
    // Track carousel navigation
    posthog?.capture('card_payment_carousel_next', {
      device_type: isMobile ? 'mobile' : 'desktop',
      from_image_index: currentImageIndex,
      to_image_index: newIndex,
      image_title: terminalImages[newIndex].title,
      navigation_type: 'next_button',
      timestamp: new Date().toISOString()
    });
  };

  const prevImage = () => {
    const newIndex = currentImageIndex === 0 ? terminalImages.length - 1 : currentImageIndex - 1;
    setCurrentImageIndex(newIndex);
    setViewedImages(prev => new Set([...prev, newIndex]));
    
    // Track carousel navigation
    posthog?.capture('card_payment_carousel_previous', {
      device_type: isMobile ? 'mobile' : 'desktop',
      from_image_index: currentImageIndex,
      to_image_index: newIndex,
      image_title: terminalImages[newIndex].title,
      navigation_type: 'previous_button',
      timestamp: new Date().toISOString()
    });
  };

  const goToImage = (index) => {
    const previousIndex = currentImageIndex;
    setCurrentImageIndex(index);
    setViewedImages(prev => new Set([...prev, index]));
    
    // Track dot navigation
    posthog?.capture('card_payment_carousel_dot_clicked', {
      device_type: isMobile ? 'mobile' : 'desktop',
      from_image_index: previousIndex,
      to_image_index: index,
      image_title: terminalImages[index].title,
      navigation_type: 'dot_navigation',
      timestamp: new Date().toISOString()
    });
  };

  // Track when features section comes into view
  const handleFeaturesView = () => {
    if (!hasViewedFeatures) {
      setHasViewedFeatures(true);
      posthog?.capture('card_payment_features_viewed', {
        device_type: isMobile ? 'mobile' : 'desktop',
        features_count: cardPaymentFeatures.length,
        timestamp: new Date().toISOString()
      });
    }
  };

  // Track individual feature card interactions
  const handleFeatureCardClick = (feature, index) => {
    posthog?.capture('card_payment_feature_card_clicked', {
      device_type: isMobile ? 'mobile' : 'desktop',
      feature_title: feature.title,
      feature_index: index,
      feature_icon: feature.icon,
      timestamp: new Date().toISOString()
    });
  };

  // Track contact section view
  const handleContactView = () => {
    posthog?.capture('card_payment_contact_viewed', {
      device_type: isMobile ? 'mobile' : 'desktop',
      contact_email: 'office@simons.rs',
      contact_phone: '+38163510605',
      contact_website: 'intervend.rs',
      timestamp: new Date().toISOString()
    });
  };

  // Track phone call clicks
  const handlePhoneClick = (location) => {
    posthog?.capture('card_payment_phone_clicked', {
      device_type: isMobile ? 'mobile' : 'desktop',
      phone_number: '+38163510605',
      click_location: location, // 'banner' or 'modal'
      timestamp: new Date().toISOString()
    });
  };

  // Track email clicks
  const handleEmailClick = () => {
    posthog?.capture('card_payment_email_clicked', {
      device_type: isMobile ? 'mobile' : 'desktop',
      email: 'office@simons.rs',
      timestamp: new Date().toISOString()
    });
  };

  // Track website clicks
  const handleWebsiteClick = () => {
    posthog?.capture('card_payment_website_clicked', {
      device_type: isMobile ? 'mobile' : 'desktop',
      website: 'intervend.rs',
      timestamp: new Date().toISOString()
    });
  };

  // Only render banner if user has showAd enabled
  if (!user?.showAd) {
    return null;
  }

  return (
    <>
      <div className={styles.banner} onClick={handleBannerClick}>
        <div className={styles.bannerContent}>
          <div className={styles.bannerLeftSection}>
            <div className={styles.textWrapper}>
              <h3 className={styles.bannerTitle}>OMOGUĆITE PLAĆANJE KARTICOM NA VAŠOJ AUTOPERIONICI</h3>
            </div>
            <div className={styles.bannerActions}>
              <div className={styles.phoneCallWrapper}>
                <a 
                  href="tel:+38163510605" 
                  className={styles.phoneCallButton} 
                  onClick={(e) => {
                    e.stopPropagation();
                    handlePhoneClick('banner');
                  }}
                >
                  <span className={styles.phoneText}>Pozovite nas</span>
                </a>
              </div>
              <div className={styles.actionWrapper}>
                <span className={styles.actionText}>Kliknite za više informacija</span>
                <span className={styles.arrow}>→</span>
              </div>
            </div>
          </div>
          <div className={styles.bannerRightSection}>
            <div className={styles.terminalImageWrapper}>
              <img 
                src="https://intervend-ads.s3.eu-central-1.amazonaws.com/701889_38227958.png" 
                alt="Kartični terminal za autoperionica"
                className={styles.terminalImage}
              />
            </div>
          </div>
        </div>
      </div>

      <Modal
        open={isModalOpen}
        onClose={handleCloseModal}
      >
        <div className={styles.modalWrap}>
          <div className={styles.modalInner}>
            <div className={styles.modalHeader}>
              <h2 className={styles.modalTitle}>Sistemi kartičnog plaćanja za autoperionice</h2>
              <div className={styles.closeIconWrap} onClick={handleCloseModal}>
                <img
                  src={CloseIcon}
                  alt="close"
                  className={styles.closeIcon}
                />
              </div>
            </div>
            
            <div className={styles.modalContent}>
              <div className={styles.featuresContent}>
                <div 
                  className={styles.featuresGrid}
                  onMouseEnter={handleFeaturesView}
                  onTouchStart={handleFeaturesView}
                >
                  {cardPaymentFeatures.map((feature, index) => (
                    <div 
                      key={index} 
                      className={styles.featureCard}
                      onClick={() => handleFeatureCardClick(feature, index)}
                    >
                      <div className={styles.featureIcon}>{feature.icon}</div>
                      <h4 className={styles.featureTitle}>{feature.title}</h4>
                      <p className={styles.featureDescription}>{feature.description}</p>
                    </div>
                  ))}
                </div>

                {/* Image Carousel - Commented out until images are available */}
                {/*
                <div className={styles.carouselSection}>
                  <h3 className={styles.carouselTitle}>Naši kartični terminali</h3>
                  <div className={styles.carousel}>
                    <div className={styles.carouselContainer}>
                      <button className={styles.carouselButton} onClick={prevImage}>
                        ‹
                      </button>
                      
                      <div className={styles.carouselImageContainer}>
                        <img
                          src={terminalImages[currentImageIndex].src}
                          alt={terminalImages[currentImageIndex].alt}
                          className={styles.carouselImage}
                        />
                        <div className={styles.imageOverlay}>
                          <h4 className={styles.imageTitle}>
                            {terminalImages[currentImageIndex].title}
                          </h4>
                        </div>
                      </div>
                      
                      <button className={styles.carouselButton} onClick={nextImage}>
                        ›
                      </button>
                    </div>
                    
                    <div className={styles.carouselDots}>
                      {terminalImages.map((_, index) => (
                        <button
                          key={index}
                          className={`${styles.dot} ${index === currentImageIndex ? styles.activeDot : ''}`}
                          onClick={() => goToImage(index)}
                        />
                      ))}
                    </div>
                  </div>
                </div>
                */}
                
                <div className={styles.actionSection}>
                  <div className={styles.pdfButtonContainer}>
                    <button 
                      className={styles.pdfButton}
                      onClick={handleOpenPDF}
                    >
                      📄 Pogledajte detaljnu brošuru (PDF)
                    </button>
                  </div>
                  
                  <div 
                    className={styles.contactSection}
                    onMouseEnter={handleContactView}
                    onTouchStart={handleContactView}
                  >
                    <h3>Kontaktirajte nas za više informacija</h3>
                    <div className={styles.contactDetails}>
                      <p>
                        <strong>Email:</strong> 
                        <a 
                          href="mailto:office@simons.rs" 
                          className={styles.emailLink}
                          onClick={handleEmailClick}
                        >
                          office@simons.rs
                        </a>
                      </p>
                      <p>
                        <strong>Telefon:</strong> 
                        <a 
                          href="tel:+38163510605" 
                          className={styles.phoneLink}
                          onClick={() => handlePhoneClick('modal')}
                        >
                          +381 63 510 605
                        </a>
                      </p>
                      <p>
                        <strong>Web:</strong> 
                        <a 
                          href="https://intervend.rs" 
                          target="_blank"
                          rel="noopener noreferrer"
                          className={styles.websiteLink}
                          onClick={handleWebsiteClick}
                        >
                          intervend.rs
                        </a>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default CardPaymentBanner; 